import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import SharedCommentsPanel from '../../comments/SharedCommentsPanel';
import SharedChatPanel from '../../chat/SharedChatPanel';
import { sharedChatUnreadCount, sharedKeys } from '../sharedAccessApi';

const TABS = [
  { key: 'comments', label: 'Comments' },
  { key: 'chat', label: 'Chat' },
];

/**
 * Same shape as ConversationSidebar, but for a file reached via an email-share
 * grant rather than workspace membership — only rendered when the resolved
 * grant includes chat access (`meta.canChat`), and only post-login (never on
 * the public /invite/:token preview).
 */
export default function SharedConversationSidebar({ fileId }) {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('comments');
  const [visited, setVisited] = useState({ comments: true, chat: false });

  // Polled independently of the chat panel itself, so a message that arrives
  // while the Chat tab is closed (or never opened) still raises a bubble here.
  const unreadKey = sharedKeys.unread(fileId);
  const { data: chatUnread = 0 } = useQuery({
    queryKey: unreadKey,
    queryFn: () => sharedChatUnreadCount(fileId),
    enabled: Boolean(fileId),
    refetchInterval: 15_000,
  });

  const select = (key) => {
    setTab(key);
    setVisited((v) => ({ ...v, [key]: true }));
    if (key === 'chat') queryClient.invalidateQueries({ queryKey: unreadKey });
  };

  return (
    <aside className="flex w-[360px] shrink-0 flex-col overflow-hidden border-l border-slate-200 bg-slate-100">
      <div className="flex shrink-0 border-b border-slate-200">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => select(key)}
            className={clsx(
              'flex-1 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
              tab === key
                ? 'border-brand-500 text-slate-800'
                : 'border-transparent text-slate-400 hover:text-slate-600',
            )}
          >
            {label}
            {key === 'chat' && tab !== 'chat' && chatUnread > 0 && (
              <span className="ml-1.5 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-semibold text-white">
                {chatUnread > 9 ? '9+' : chatUnread}
              </span>
            )}
          </button>
        ))}
      </div>

      {visited.comments && (
        <div hidden={tab !== 'comments'} className="min-h-0 flex-1 p-3">
          <SharedCommentsPanel fileId={fileId} />
        </div>
      )}
      {visited.chat && (
        <div hidden={tab !== 'chat'} className="min-h-0 flex-1">
          <SharedChatPanel fileId={fileId} />
        </div>
      )}
    </aside>
  );
}
